import React, { useState, useMemo, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';

// 🎨 Paleta para los estados (variada, más de 2 categorías)
const PALETA = ['#0284c7', '#3b82f6', '#10b981', '#84cc16', '#8cd45c', '#f59e0b', '#64748b', '#94a3b8'];

export const GraficoTortaEstados = ({ data = [], estadosFiltro = [], heightClass = 'h-[380px]' }) => {

	// 🔵 Selección local de la torta (patrón RED: toggle simple)
	const [seleccionEstado, setSeleccionEstado] = useState(null);
	// 🔁 Vista: 'torta' | 'barras'
	const [vista, setVista] = useState('torta');

	const clickEstado = useCallback((params) => {
		const nombre = params?.name;
		if (!nombre) return;
		setSeleccionEstado(prev => (prev === nombre ? null : nombre));
	}, []);

	// 💡 Devuelve true si el item debe quedar opacado (0.25) según la selección local o el filtro global
	const dimItem = useCallback((nombre) => {
		if (seleccionEstado) return seleccionEstado !== nombre;
		if (Array.isArray(estadosFiltro) && estadosFiltro.length > 0 && !estadosFiltro.includes('Todos')) {
			return !estadosFiltro.includes(nombre);
		}
		return false;
	}, [seleccionEstado, estadosFiltro]);

	const chartData = useMemo(() =>
		(data || []).map((item, i) => ({
			name: item.estado || 'Sin Especificar',
			value: parseInt(item.cantidad || 0),
			itemStyle: {
				color: PALETA[i % PALETA.length],
				opacity: dimItem(item.estado || 'Sin Especificar') ? 0.25 : 1,
			},
		})),
		[data, dimItem]
	);

	const optionTorta = useMemo(() => ({
		tooltip: {
			trigger: 'item',
			backgroundColor: '#1e293b',
			borderWidth: 0,
			textStyle: { color: '#ffffff', fontSize: 12 },
			padding: [10, 15],
			formatter: '{b}: {c} ({d}%)',
		},
		legend: {
			type: 'scroll',
			orient: 'vertical',
			right: '5%',
			top: 'center',
			itemGap: 12,
			textStyle: { color: '#64748b', fontSize: 12 },
			formatter: function (name) {
				const encontrado = chartData.find(d => d.name === name);
				const cantidad = encontrado ? encontrado.value : 0;
				const nombreCorto = name.length > 30 ? name.substring(0, 27) + '...' : name;
				return `${nombreCorto} (${cantidad})`;
			},
		},
		series: [{
			name: 'Estados',
			type: 'pie',
			radius: ['40%', '70%'],
			center: ['30%', '50%'],
			minAngle: 12,
			avoidLabelOverlap: true,
			itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
			label: { show: false, position: 'center' },
			emphasis: {
				label: {
					show: true,
					fontSize: 16,
					fontWeight: 'bold',
					formatter: '{b}',
				},
			},
			labelLine: { show: false },
			data: chartData,
		}],
	}), [chartData]);

	// 📊 Barras verticales con el mismo patrón de selección (patrón RED)
	const optionBarras = useMemo(() => ({
		tooltip: {
			trigger: 'axis',
			axisPointer: { type: 'shadow' },
			backgroundColor: '#1e293b',
			borderWidth: 0,
			textStyle: { color: '#ffffff', fontSize: 12 },
			padding: [10, 15],
			formatter: '{b}: <b>{c} proyectos</b>',
		},
		grid: { left: '4%', right: '4%', bottom: '3%', containLabel: true },
		xAxis: {
			type: 'category',
			data: chartData.map(d => d.name),
			axisLabel: {
				fontSize: 10,
				color: '#64748b',
				// 🔥 Rotamos y recortamos etiquetas largas para que no desborden
				interval: 0,
				rotate: 30,
				formatter: function (value) {
					return value.length > 18 ? value.substring(0, 16) + '...' : value;
				},
			},
		},
		yAxis: {
			type: 'value',
			splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } },
			axisLabel: { color: '#64748b' },
		},
		series: [{
			name: 'Proyectos',
			type: 'bar',
			barWidth: '45%',
			data: chartData.map(d => ({
				value: d.value,
				itemStyle: {
					color: d.itemStyle.color,
					opacity: d.itemStyle.opacity,
					borderRadius: [4, 4, 0, 0],
				},
			})),
			label: {
				show: true,
				position: 'top',
				fontSize: 11,
				fontWeight: 'bold',
				color: '#374151',
			},
		}],
	}), [chartData]);

	const onEvents = useMemo(() => ({ click: clickEstado }), [clickEstado]);

	return (
		<div className={`bg-white p-6 rounded-xl border border-gray-200 shadow-sm ${heightClass} flex flex-col`}>
			<div className="mb-2 flex items-start justify-between">
				<div>
					<h3 className="text-base font-bold text-gray-800 tracking-tight uppercase">
						Proyectos por Estado
					</h3>
					<p className="text-xs text-gray-400">
						Distribución de proyectos según su estado (hacé click para resaltar, el resto se opaca)
					</p>
				</div>
				{/* 🔁 Toggle Torta / Barras */}
				<div className="flex rounded-lg border border-gray-200 overflow-hidden">
					<button
						type="button"
						onClick={() => setVista('torta')}
						className={`px-3 py-1 text-xs font-medium transition-colors ${vista === 'torta' ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
					>
						Torta
					</button>
					<button
						type="button"
						onClick={() => setVista('barras')}
						className={`px-3 py-1 text-xs font-medium transition-colors ${vista === 'barras' ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
					>
						Barras
					</button>
				</div>
			</div>

			<div className="flex-1 w-full min-h-0">
				<ReactECharts
					option={vista === 'torta' ? optionTorta : optionBarras}
					onEvents={onEvents}
					style={{ height: '100%', width: '100%' }}
					notMerge={true}
				/>
			</div>
		</div>
	);
};